/**
 * Knowledge Base Management Page (Admin)
 *
 * Admin page for managing knowledge base articles and FAQs.
 * Provides CRUD operations for content management.
 */

import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Eye } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Badge } from '../../components/common/Badge';
import { EmptyState } from '../../components/common/EmptyState';
import type { KnowledgeBaseArticle, FAQ, KnowledgeBaseCategory } from '../../types/support';

export const KnowledgeBaseManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'articles' | 'faqs'>('articles');
  const [articles, setArticles] = useState<KnowledgeBaseArticle[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<KnowledgeBaseCategory | 'all'>('all');

  useEffect(() => {
    loadData();
  }, [activeTab, categoryFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API calls when Phase 1E is implemented
      if (activeTab === 'articles') {
        // const response = await mockApi.getKnowledgeBaseArticles(categoryFilter !== 'all' ? categoryFilter : undefined);
        setArticles([]);
      } else {
        // const response = await mockApi.getFAQs(categoryFilter !== 'all' ? categoryFilter : undefined);
        setFaqs([]);
      }
    } catch (error) {
      console.error('Failed to load knowledge base data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArticle = async (_id: string) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      // TODO: Implement delete API call
      // await mockApi.deleteArticle(id);
      await loadData();
    } catch (error) {
      console.error('Failed to delete article:', error);
    }
  };

  const handleDeleteFAQ = async (_id: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      // TODO: Implement delete API call
      // await mockApi.deleteFAQ(id);
      await loadData();
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
    }
  };

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'getting_started', label: 'Getting Started' },
    { value: 'application_process', label: 'Application Process' },
    { value: 'audit_preparation', label: 'Audit Preparation' },
    { value: 'training', label: 'Training' },
    { value: 'payments_invoices', label: 'Payments & Invoices' },
    { value: 'certificates', label: 'Certificates' },
    { value: 'renewal', label: 'Renewal' },
    { value: 'technical', label: 'Technical' },
    { value: 'policies', label: 'Policies' },
  ];

  const filteredArticles = articles.filter((article) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        article.title.toLowerCase().includes(query) ||
        article.excerpt.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const filteredFAQs = faqs.filter((faq) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <PageLayout
      pageTitle="Knowledge Base Management"
      breadcrumbs={[
        { label: 'Home', href: '/' },
        { label: 'Admin', href: '/admin/dashboard' },
        { label: 'Support', href: '/admin/support' },
        { label: 'Knowledge Base' },
      ]}
      actions={
        <Button
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => {
            // TODO: Open create modal
            console.log('Create new', activeTab);
          }}
        >
          Create {activeTab === 'articles' ? 'Article' : 'FAQ'}
        </Button>
      }
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'articles' ? 'primary' : 'outline'}
          onClick={() => setActiveTab('articles')}
        >
          Articles ({articles.length})
        </Button>
        <Button
          variant={activeTab === 'faqs' ? 'primary' : 'outline'}
          onClick={() => setActiveTab('faqs')}
        >
          FAQs ({faqs.length})
        </Button>
      </div>

      {/* Search and Filter */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={<Search className="w-4 h-4" />}
              />
            </div>
            <div>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as KnowledgeBaseCategory | 'all')}
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Articles List */}
      {activeTab === 'articles' && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">
              Articles
              {filteredArticles.length > 0 && (
                <span className="ml-2 text-sm font-normal text-neutral-600">
                  ({filteredArticles.length})
                </span>
              )}
            </h3>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-20 bg-neutral-200 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : filteredArticles.length === 0 ? (
              <EmptyState
                icon={<Plus className="w-10 h-10" />}
                title={searchQuery ? 'No Articles Found' : 'No Articles'}
                description={
                  searchQuery ? 'Try different keywords.' : 'Create your first article to get started.'
                }
                action={{
                  label: 'Create Article',
                  onClick: () => console.log('Create article'),
                  icon: <Plus className="w-4 h-4" />,
                }}
              />
            ) : (
              <div className="space-y-2">
                {filteredArticles.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">{article.title}</h4>
                        {article.featured && (
                          <Badge variant="warning" size="sm">
                            Featured
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-neutral-700 mb-2">{article.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-neutral-600">
                        <div className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          <span>{article.viewCount} views</span>
                        </div>
                        <span>•</span>
                        <span>{article.helpful} helpful</span>
                        <span>•</span>
                        <span>
                          {new Date(article.updatedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Edit2 className="w-4 h-4" />}
                        onClick={() => console.log('Edit article', article.id)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Trash2 className="w-4 h-4" />}
                        onClick={() => handleDeleteArticle(article.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* FAQs List */}
      {activeTab === 'faqs' && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">
              FAQs
              {filteredFAQs.length > 0 && (
                <span className="ml-2 text-sm font-normal text-neutral-600">
                  ({filteredFAQs.length})
                </span>
              )}
            </h3>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-neutral-200 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : filteredFAQs.length === 0 ? (
              <EmptyState
                icon={<Plus className="w-10 h-10" />}
                title={searchQuery ? 'No FAQs Found' : 'No FAQs'}
                description={
                  searchQuery ? 'Try different keywords.' : 'Create your first FAQ to get started.'
                }
                action={{
                  label: 'Create FAQ',
                  onClick: () => console.log('Create FAQ'),
                  icon: <Plus className="w-4 h-4" />,
                }}
              />
            ) : (
              <div className="space-y-2">
                {filteredFAQs.map((faq) => (
                  <div
                    key={faq.id}
                    className="flex items-start justify-between p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                  >
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{faq.question}</h4>
                      <p className="text-sm text-neutral-700 line-clamp-2 mb-2">{faq.answer.replace(/<[^>]*>/g, '')}</p>
                      <div className="flex items-center gap-3 text-xs text-neutral-600">
                        <span>{faq.helpful} helpful</span>
                        <span>•</span>
                        <span>{faq.notHelpful} not helpful</span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Edit2 className="w-4 h-4" />}
                        onClick={() => console.log('Edit FAQ', faq.id)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Trash2 className="w-4 h-4" />}
                        onClick={() => handleDeleteFAQ(faq.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </PageLayout>
  );
};

